
import { defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { companies } from '@/core/realisations/companies.realisation'
import { CompanyT } from '@/core/types/companies.types'

export default defineComponent({
    components: {
        modal: Modal,
        'the-select': TheSelect,
        checkbox: Checkbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        selectedCompanies: {
            type: Array as PropType<{ id: number; number: string }[]>,
            default: () => [],
        },
        companyId: {
            type: Number,
            default: null,
        },
    },
    emits: ['cancel', 'payers'],
    setup(props, { emit }) {
        const state = reactive({
            payer_company_ids: [] as number[],
            payer_company_data: [] as any[],
        })

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
        })

        const emitCancel = () => {
            emit('cancel')
        }

        const emitPayers = () => {
            emit('payers', state.payer_company_data)
        }

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const params = {
                    page,
                    search,
                    limit: 9999,
                    statusIds: [1, 2],
                    isExtended: 0,
                }

                const response = await companies.getAllList(params)

                let selected_payers_ids = response.data.find(
                    (company) => company.id === props.companyId
                )?.payer_companies

                data_state.companies.list = response.data.map((company) => {
                    company.use_tariff = selected_payers_ids?.find(
                        (p_c) => p_c.use_tariff && p_c.id === company.id
                    )
                        ? 1
                        : 0
                    return company
                })

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, statusId: 1 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        const handleSelectCheckbox = (option: any) => {
            let selected_company = state.payer_company_data.find(
                (payer_company) => payer_company.id === option.id
            )
            selected_company.use_tariff = selected_company.use_tariff ? 0 : 1
        }

        const handleSelect = (selected_payer_company_id: any) => {
            let payer_company = state.payer_company_data.find(
                (p_c) => p_c.id === selected_payer_company_id
            )

            if (!payer_company) {
                state.payer_company_data.push({
                    id: selected_payer_company_id,
                    use_tariff: 0,
                })
            } else {
                let selected_company = data_state.companies.list.find(
                    (company) => company.id === selected_payer_company_id
                )
                if (selected_company) selected_company.use_tariff = 0

                const index = state.payer_company_data.findIndex(
                    (value) => value.id === selected_payer_company_id
                )
                state.payer_company_data.splice(index, 1)
            }
        }

        onBeforeMount(async () => {
            await loadCompanies(1)

            state.payer_company_ids = props.selectedCompanies.map((company) => company.id)
            state.payer_company_data = state.payer_company_ids.map((id) => {
                return {
                    id,
                    use_tariff: data_state.companies.list.find((company) => company.id === id)
                        ?.use_tariff
                        ? 1
                        : 0,
                }
            })
        })

        return {
            state,
            data_state,
            emitCancel,
            emitPayers,
            handleSelectCheckbox,
            handleSelect,
            // companies
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
        }
    },
})
